import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiTrash2, FiFileText, FiMapPin, FiCalendar, FiTarget, FiCheck } from 'react-icons/fi';
import Modal from '../../../components/Modal';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Select from '../../../components/Select';
import MultiSelect from '../../../components/MultiSelect';
import { toast } from 'react-toastify';
import api from '../../../Api';
import '../../../components/CompanyForm/style.css';
import './style.css';

const DAYS = [
  { key: 'monday', label: 'Segunda', short: 'Seg' },
  { key: 'tuesday', label: 'Terça', short: 'Ter' },
  { key: 'wednesday', label: 'Quarta', short: 'Qua' },
  { key: 'thursday', label: 'Quinta', short: 'Qui' },
  { key: 'friday', label: 'Sexta', short: 'Sex' },
  { key: 'saturday', label: 'Sábado', short: 'Sáb' },
  { key: 'sunday', label: 'Domingo', short: 'Dom' },
];

export default function RoundModal({ isOpen, onClose, initialData, serviceStations, companies = [], onSuccess, companyId }) {
  const [name, setName] = useState('');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [selectedStationIds, setSelectedStationIds] = useState([]);
  const [periodicity, setPeriodicity] = useState({});
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const pointsContainerRef = useRef(null);
  const shouldScrollRef = useRef(false);

  // Delete Confirmation State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pointToDeleteIndex, setPointToDeleteIndex] = useState(null);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      const initialCompanyId =
        initialData.company_id ||
        initialData.companyId ||
        initialData.company?.id ||
        companyId ||
        null;

      setSelectedCompanyIds(initialCompanyId ? [String(initialCompanyId)] : []);
      setSelectedStationIds(
        initialData.service_station_id ? [String(initialData.service_station_id)] : []
      );
      setPeriodicity(initialData.periodicity || initializePeriodicity());
      setPoints(initialData.points || []);
    } else {
      resetForm();
    }
  }, [initialData, isOpen]);

  useEffect(() => {
    if (shouldScrollRef.current && pointsContainerRef.current) {
      // Scroll the container's bottom into view, effectively scrolling the modal down
      pointsContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
      shouldScrollRef.current = false;
    }
  }, [points]);

  function initializePeriodicity() {
    const p = {};
    DAYS.forEach(day => {
      p[day.key] = { active: false, quantity: 1 };
    });
    return p;
  }

  function resetForm() {
    setName('');
    setSelectedCompanyIds([]);
    setSelectedStationIds([]);
    setPeriodicity(initializePeriodicity());
    setPoints([]);
  }

  function handlePeriodicityChange(dayKey, field, value) {
    setPeriodicity(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: value,
      },
    }));
  }

  function toggleDay(dayKey) {
    handlePeriodicityChange(dayKey, 'active', !periodicity[dayKey]?.active);
  }

  function handleAddPoint() {
    setPoints([...points, { name: '', active: true }]);
    shouldScrollRef.current = true;
  }

  function handleRemovePoint(index) {
    setPointToDeleteIndex(index);
    setDeleteModalOpen(true);
  }

  function confirmDeletePoint() {
    if (pointToDeleteIndex !== null) {
      const newPoints = [...points];
      newPoints.splice(pointToDeleteIndex, 1);
      setPoints(newPoints);
      setDeleteModalOpen(false);
      setPointToDeleteIndex(null);
    }
  }

  function handlePointChange(index, value) {
    const newPoints = [...points];
    newPoints[index].name = value;
    setPoints(newPoints);
  }

  function handleCompanyChange(newValues) {
    if (initialData) {
      const first = Array.isArray(newValues) && newValues.length > 0 ? String(newValues[0]) : null;
      setSelectedCompanyIds(first ? [first] : []);
    } else {
      setSelectedCompanyIds(Array.isArray(newValues) ? newValues.map(v => String(v)) : []);
    }
  }

  function handleStationChange(newValues) {
    if (initialData) {
      const first = Array.isArray(newValues) && newValues.length > 0 ? String(newValues[0]) : null;
      setSelectedStationIds(first ? [first] : []);
    } else {
      setSelectedStationIds(Array.isArray(newValues) ? newValues.map(v => String(v)) : []);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!name || selectedCompanyIds.length === 0 || selectedStationIds.length === 0) {
      toast.error('Nome, Empresa e Posto de Serviço são obrigatórios.');
      return;
    }

    if (points.some(p => !p.name.trim())) {
      toast.error('Todos os pontos devem ter um nome.');
      return;
    }

    try {
      setLoading(true);
      const basePayload = {
        name,
        periodicity,
        points,
      };

      if (initialData) {
        const stationId =
          selectedStationIds[0] || String(initialData.service_station_id || '');

        if (!stationId || !companyId) {
          toast.error('Erro ao identificar empresa ou posto da ronda.');
          return;
        }

        const payload = {
          ...basePayload,
          service_station_id: stationId,
        };

        await api.put(`/companies/${companyId}/rounds/${initialData.id}`, payload);
        toast.success('Ronda atualizada com sucesso!');
      } else {
        for (const id of selectedStationIds) {
          const station = serviceStations.find(s => String(s.id) === String(id));
          if (!station) {
            continue;
          }

          const stationCompanyId =
            station.company_id ||
            station.companyId ||
            station.company?.id ||
            companyId;

          if (!stationCompanyId) {
            console.warn('Nenhuma empresa vinculada ao posto de serviço', station);
            continue;
          }

          const payload = {
            ...basePayload,
            service_station_id: id,
          };

          await api.post(`/companies/${stationCompanyId}/rounds`, payload);
        }

        toast.success('Ronda(s) criada(s) com sucesso!');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar ronda.');
    } finally {
      setLoading(false);
    }
  }

  const companyOptions = companies.map(c => ({
    value: String(c.id),
    label: c.corporate_name || c.trade_name || c.name || `Empresa ${c.id}`,
  }));

  const stationOptions = serviceStations
    .filter(s => {
      const stationCompanyId =
        s.company_id ||
        s.companyId ||
        s.company?.id ||
        null;

      if (!stationCompanyId) return false;
      if (selectedCompanyIds.length === 0) return false;

      return selectedCompanyIds.includes(String(stationCompanyId));
    })
    .map(s => {
    const company =
      companies.find(c => String(c.id) === String(s.company_id)) || s.company;
    const companyName = company
      ? company.corporate_name || company.trade_name || company.name || `Empresa ${company.id}`
      : '';

    return {
      value: String(s.id),
      label: companyName ? `${s.name} - ${companyName}` : s.name,
    };
    });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Ronda' : 'Nova Ronda'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="company-form-component">
        <div className="form-body">
          <h3 className="section-title" style={{ marginTop: 0 }}>Dados da Ronda</h3>
          <div className="form-grid">
            <div className="full-width">
              <Input
                label="Nome da Ronda"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Ronda Noturna"
                required
                icon={FiFileText}
              />
            </div>
            <div className="full-width">
              {initialData ? (
                <Input
                  label="Empresa"
                  value={
                    (() => {
                      const company =
                        companies.find(c => String(c.id) === String(selectedCompanyIds[0])) ||
                        initialData.company ||
                        null;
                      if (!company) return '';
                      return (
                        company.corporate_name ||
                        company.trade_name ||
                        company.name ||
                        `Empresa ${company.id}`
                      );
                    })()
                  }
                  disabled
                />
              ) : (
                <MultiSelect
                  label="Empresas"
                  options={companyOptions}
                  value={selectedCompanyIds}
                  onChange={handleCompanyChange}
                  placeholder="SELECIONE..."
                  searchable
                />
              )}
            </div>
            <div className="full-width">
              {initialData ? (
                <Select
                  label="Posto de Serviço"
                  options={stationOptions}
                  value={selectedStationIds[0] || ''}
                  onChange={(e) => handleStationChange([e.target.value])}
                  placeholder="SELECIONE..."
                  icon={FiMapPin}
                  searchable
                />
              ) : (
                <MultiSelect
                  label="Postos de Serviço"
                  options={stationOptions}
                  value={selectedStationIds}
                  onChange={handleStationChange}
                  placeholder="SELECIONE..."
                  icon={FiMapPin}
                  searchable
                />
              )}
            </div>
          </div>

          <h3 className="section-title">
            <FiCalendar style={{ marginRight: '8px' }} />
            Periodicidade
          </h3>
          <p className="section-subtitle">Selecione os dias da semana e a quantidade de rondas por dia.</p>
          
          <div className="periodicity-grid">
            {DAYS.map(day => {
              const isActive = periodicity[day.key]?.active;
              return (
                <div 
                  key={day.key} 
                  className={`day-card ${isActive ? 'active' : ''}`}
                  onClick={() => toggleDay(day.key)}
                >
                  <div className="day-header">
                    <span className="day-label">{day.short}</span>
                    <div className={`toggle-indicator ${isActive ? 'on' : 'off'}`}>
                      {isActive && <FiCheck size={12} color="white" />}
                    </div>
                  </div>
                  
                  {isActive ? (
                    <div className="day-content" onClick={(e) => e.stopPropagation()}>
                      <label>Quantidade</label>
                      <Input
                        type="number"
                        min="1"
                        value={periodicity[day.key]?.quantity}
                        onChange={(e) => handlePeriodicityChange(day.key, 'quantity', parseInt(e.target.value))}
                        className="qty-input"
                      />
                    </div>
                  ) : (
                    <div className="day-placeholder">
                      <span>-</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="section-header-row">
            <h3 className="section-title" style={{ borderBottom: 'none', margin: 0 }}>
              <FiTarget style={{ marginRight: '8px' }} />
              Pontos de Verificação
            </h3>
            <Button type="button" onClick={handleAddPoint} size="sm" variant="secondary" className="add-point-btn">
              <FiPlus /> Adicionar Ponto
            </Button>
          </div>
          
          <div className="points-container" ref={pointsContainerRef}>
            {points.length === 0 ? (
              <div className="empty-points">
                <p>Nenhum ponto de verificação definido.</p>
                <Button type="button" onClick={handleAddPoint} variant="outline" size="sm">
                  Começar a adicionar
                </Button>
              </div>
            ) : (
              <div className="points-list">
                {points.map((point, index) => (
                  <div key={index} className="point-row">
                    <div className="point-number">{index + 1}</div>
                    <div className="point-input-wrapper">
                      <Input
                        value={point.name}
                        onChange={(e) => handlePointChange(index, e.target.value)}
                        placeholder="Nome do Ponto (ex: Portão Principal)"
                        icon={FiTarget}
                      />
                    </div>
                    <button 
                      type="button" 
                      className="remove-point-btn" 
                      onClick={() => handleRemovePoint(index)}
                      title="Remover ponto"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button type="submit" loading={loading} disabled={loading}>
            {initialData ? 'Salvar Alterações' : 'Criar Ronda'}
          </Button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirmar Exclusão"
        size="md"
      >
        <div className="delete-modal-content">
          <p>Tem certeza que deseja excluir este ponto?</p>
          <p className="warning-text">
            O QR Code deste ponto ficará inválido após a exclusão.
          </p>
        </div>
        <div className="delete-modal-actions">
          <Button 
            type="button"
            className="cancel-button"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancelar
          </Button>
          <Button 
            type="button"
            onClick={confirmDeletePoint}
            className="delete-button"
          >
            Excluir
          </Button>
        </div>
      </Modal>
    </Modal>
  );
}
